import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, Stethoscope, Users, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Landing = () => {
  const navigate = useNavigate();

  const handleGetStarted = () => {
    navigate('/register');
  };

  const handleLogin = () => {
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f0f7ff] via-white to-[#f8f9fb]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#2563eb] to-[#1e40af] flex items-center justify-center">
              <Heart className="w-6 h-6 text-white" fill="currentColor" />
            </div>
            <span className="text-xl font-bold text-gray-900">Healthcare Hearts</span>
          </div>
          <Button 
            onClick={handleLogin}
            data-testid="login-button"
            className="bg-[#2563eb] hover:bg-[#1e40af] text-white px-6 py-2 rounded-lg font-medium transition-all duration-200 hover:scale-105"
          >
            Sign In
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-blue-50 text-[#2563eb] px-4 py-2 rounded-full text-sm font-medium mb-8">
            <Shield className="w-4 h-4" />
            <span>Meet Verified Healthcare Professionals</span>
          </div>
          
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            Find Your Match in <br />
            <span className="bg-gradient-to-r from-[#2563eb] to-[#0ea5e9] bg-clip-text text-transparent">
              Healthcare Shortage Areas
            </span>
          </h1>
          
          <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-10">
            Connect with verified doctors, nurses, and healthcare professionals serving communities that need them most. 
            Build meaningful relationships while making a difference.
          </p>
          
          <Button 
            onClick={handleGetStarted}
            data-testid="hero-cta-button"
            className="bg-[#2563eb] hover:bg-[#1e40af] text-white px-8 py-4 rounded-xl text-lg font-semibold shadow-lg shadow-blue-500/30 transition-all duration-200 hover:scale-105 hover:shadow-xl"
          >
            Get Started
          </Button>
        </div>
      </section>

      {/* Pricing Preview */}
      <section className="py-16 px-6 bg-gradient-to-b from-white to-blue-50">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            Simple Pricing
          </h2>
          <p className="text-lg text-gray-600 mb-12">
            One plan with everything you need
          </p>
          
          <div className="max-w-md mx-auto bg-white rounded-2xl shadow-xl border-2 border-[#2563eb] p-8">
            <div className="inline-block bg-green-100 text-green-800 text-sm font-semibold px-4 py-1 rounded-full mb-4">
              FIRST MONTH FREE
            </div>
            <div className="mb-6">
              <div className="flex items-baseline justify-center gap-2 mb-2">
                <span className="text-5xl font-bold text-gray-900">$20</span>
                <span className="text-xl text-gray-600">/month</span>
              </div>
              <p className="text-gray-600">After free trial - Full access to all features</p>
            </div>
            
            <Button
              onClick={handleGetStarted}
              data-testid="pricing-cta-button"
              className="w-full bg-[#2563eb] hover:bg-[#1e40af] text-white px-8 py-3 rounded-lg text-lg font-semibold"
            >
              Get Started
            </Button>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-gray-900 mb-16">
            Why Healthcare Hearts?
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-blue-50 to-white p-8 rounded-2xl border border-gray-200 hover:shadow-lg transition-shadow duration-300">
              <div className="w-14 h-14 rounded-xl bg-[#2563eb] flex items-center justify-center mb-4">
                <Shield className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Verified Professionals</h3>
              <p className="text-gray-600">
                All healthcare providers are verified through their organization email addresses. Connect with confidence.
              </p>
            </div>
            
            <div className="bg-gradient-to-br from-blue-50 to-white p-8 rounded-2xl border border-gray-200 hover:shadow-lg transition-shadow duration-300">
              <div className="w-14 h-14 rounded-xl bg-[#0ea5e9] flex items-center justify-center mb-4">
                <Stethoscope className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Shortage Area Focus</h3>
              <p className="text-gray-600">
                Meet dedicated professionals serving underserved communities. Find someone who shares your values.
              </p>
            </div>
            
            <div className="bg-gradient-to-br from-blue-50 to-white p-8 rounded-2xl border border-gray-200 hover:shadow-lg transition-shadow duration-300">
              <div className="w-14 h-14 rounded-xl bg-[#06b6d4] flex items-center justify-center mb-4">
                <Users className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Community Members Welcome</h3>
              <p className="text-gray-600">
                Local community members in shortage areas can also join to connect with healthcare heroes.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gradient-to-br from-[#2563eb] to-[#1e40af] rounded-3xl p-12 shadow-2xl">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              Ready to Find Your Match?
            </h2>
            <p className="text-blue-100 text-lg mb-8">
              Join our community of healthcare professionals and find meaningful connections.
            </p>
            <Button 
              onClick={handleGetStarted}
              data-testid="footer-cta-button"
              className="bg-white text-[#2563eb] hover:bg-gray-100 px-8 py-4 rounded-xl text-lg font-semibold shadow-lg transition-all duration-200 hover:scale-105"
            >
              Sign Up Now
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#2563eb] to-[#1e40af] flex items-center justify-center">
              <Heart className="w-5 h-5 text-white" fill="currentColor" />
            </div>
            <span className="text-white font-semibold">Healthcare Hearts</span>
          </div>
          <p className="text-sm mb-4">
            © 2025 Healthcare Hearts. Connecting healthcare professionals in underserved communities.
          </p>
          <div className="flex items-center justify-center gap-4 text-sm">
            <a href="/admin/login" className="text-gray-500 hover:text-gray-300 transition-colors">
              Admin
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
